package dev.lingvo.site.components.editor

import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.TranslationState
import dev.lingvo.shared.pluralCategoriesFor
import dev.lingvo.site.i18n.tr

/**
 * The three words the queue sorts and filters by, as a person reads them.
 *
 * They are the column a translator scans, and they were branch literals — a position the extractor
 * cannot reach — so the queue kept three English words in the middle of a translated editor.
 */
internal fun editorQueueStateLabel(state: TranslationState?): String = when (state) {
    null, TranslationState.EMPTY -> tr("editor.editor_queue_presentation.untranslated", "Untranslated")
    TranslationState.TRANSLATED -> tr("editor.editor_queue_presentation.translated", "Translated")
    TranslationState.APPROVED -> tr("editor.editor_queue_presentation.approved", "Approved")
}

internal fun showCompactTargetPreview(
    workspaceClientWidth: Double,
    compactLayout: Boolean,
): Boolean = compactLayout && workspaceClientWidth >= EDITOR_COMPACT_QUEUE_DENSITY_THRESHOLD_PX

/**
 * Whether a queue row shows a preview of the target translation.
 *
 * The workbench queue is a fixed [EDITOR_QUEUE_WIDTH_PX] regardless of the overall viewport, unlike
 * the compact queue, whose pane grows and shrinks with it — so there is no density threshold to
 * gate the workbench case on: if the row has the room at one workbench width, it has it at every
 * workbench width. `MEASURING` falls through to the compact branch and returns false there, which
 * is fine either way since that state paints the workspace hidden.
 */
internal fun showQueueTargetPreview(
    renderedLayout: EditorRenderedLayout,
    queueMounted: Boolean,
    compactWorkspaceClientWidth: Double,
): Boolean = queueMounted &&
    (renderedLayout == EditorRenderedLayout.WORKBENCH ||
        showCompactTargetPreview(compactWorkspaceClientWidth, renderedLayout == EditorRenderedLayout.COMPACT))

internal data class EditorQueueRowPresentation(
    val source: String,
    val key: String,
    val state: String,
    val targetPreview: String?,
    val qaText: String?,
    val machine: Boolean,
    /** Non-null when a save on this key hit a 409 nobody has resolved yet. */
    val conflictText: String?,
)

internal fun editorQueueRowPresentation(
    row: KeyRow,
    locale: String,
    activePluralCategory: PluralCategory?,
    severity: QaSeverity?,
    hasConflict: Boolean = false,
): EditorQueueRowPresentation {
    val target = row.target
    val targetPreview = when {
        !row.key.isPlural -> target?.value
        else -> {
            val forms = target?.pluralForms.orEmpty()
            val category = activePluralCategory
                ?: pluralCategoriesFor(locale).firstOrNull { forms[it].orEmpty().isNotBlank() }
            category?.let { forms[it] }
        }
    }?.takeIf(String::isNotBlank)

    return EditorQueueRowPresentation(
        source = row.sourceText().ifBlank {
            tr("editor.editor_queue_presentation.no_source_text", "No source text")
        },
        key = row.key.name,
        state = editorQueueStateLabel(target?.state),
        targetPreview = targetPreview,
        qaText = severity?.let {
            if (it == QaSeverity.ERROR) {
                tr("editor.editor_queue_presentation.error", "Error")
            } else {
                tr("editor.editor_queue_presentation.warning", "Warning")
            }
        },
        machine = isMachineUnreviewed(target),
        conflictText = tr(
            "editor.editor_queue_presentation.unresolved_conflict",
            "Unresolved conflict",
        ).takeIf { hasConflict },
    )
}
